"use client";

import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import {
  BadgeDollarSign,
  Check,
  CircleDot,
  Copy,
  ExternalLink,
  Flag,
  Globe,
  HandCoins,
  KeyRound,
  Rocket,
  Search,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { FlagshipStatus } from "@/types/mission";
import { timeAgo } from "@/components/mission/sections";

/* ------------------------------ flagship service ---------------------------- */

const RUNG_ICONS = [Rocket, Globe, Search, Flag, HandCoins, BadgeDollarSign];

function LadderRung({
  index,
  label,
  note,
  achieved,
  isFrontier,
  lastChange,
  delay,
}: {
  index: number;
  label: string;
  note: string;
  achieved: boolean;
  isFrontier: boolean;
  lastChange: string;
  delay: number;
}) {
  const Icon = RUNG_ICONS[index] ?? CircleDot;
  return (
    <motion.li
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.3, ease: "easeOut" }}
      className="relative flex gap-3 pb-4 last:pb-0"
    >
      {/* connector rail */}
      <span
        aria-hidden="true"
        className={`absolute left-[13px] top-7 h-[calc(100%-1.4rem)] w-px ${
          achieved ? "bg-emerald-500/40" : "bg-zinc-800"
        }`}
      />
      {/* rung node */}
      <span
        className={`relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full border ${
          achieved
            ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-400"
            : isFrontier
              ? "border-amber-500/50 bg-amber-500/10 text-amber-400"
              : "border-zinc-700/70 bg-zinc-800/40 text-zinc-600"
        }`}
        title={achieved ? `achieved · since ${lastChange}` : "not yet achieved (honest state)"}
      >
        {achieved ? (
          <Check className="size-3.5" aria-hidden="true" />
        ) : (
          <Icon className={`size-3.5 ${isFrontier ? "animate-pulse" : ""}`} aria-hidden="true" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span
            className={`font-mono text-[11px] font-semibold tracking-wide ${
              achieved ? "text-zinc-200" : isFrontier ? "text-amber-300" : "text-zinc-500"
            }`}
          >
            {index + 1}. {label}
          </span>
          {achieved ? (
            <span className="font-mono text-[9px] uppercase tracking-wider text-emerald-500/80">
              since {lastChange}
            </span>
          ) : isFrontier ? (
            <span className="font-mono text-[9px] uppercase tracking-wider text-amber-400/90">
              next rung · blocked below
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-500">{note}</p>
      </div>
    </motion.li>
  );
}

export function FlagshipCard({ flagship }: { flagship: FlagshipStatus | null }) {
  const [copied, setCopied] = useState<string | null>(null);
  const { toast } = useToast();

  const copy = useCallback(
    async (key: string, text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(key);
        toast({ title: `${label} copied`, description: `${text.slice(0, 22)}…${text.slice(-8)}` });
        window.setTimeout(() => setCopied((k) => (k === key ? null : k)), 1600);
      } catch {
        toast({ title: "Copy failed", description: "Clipboard unavailable in this context" });
      }
    },
    [toast],
  );

  if (!flagship) return null;

  const achieved = flagship.ladder.filter((r) => r.achieved).length;
  const frontierIdx = flagship.ladder.findIndex((r) => !r.achieved);
  const host = flagship.public_base.replace(/^https?:\/\//, "");

  return (
    <Card className="min-w-0 border-zinc-800/70 bg-zinc-900/40 shadow-none">
      <CardHeader className="min-w-0 pb-3">
        <CardTitle className="flex min-w-0 flex-wrap items-center justify-between gap-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          <span className="flex min-w-0 items-center gap-2">
            <KeyRound className="size-3.5 shrink-0 text-emerald-400" aria-hidden="true" />
            Flagship Service · x402
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge
              variant="outline"
              className="border-emerald-500/40 bg-emerald-500/10 font-mono text-[10px] text-emerald-400"
            >
              {achieved}/{flagship.ladder.length} rungs
            </Badge>
            <Badge variant="outline" className="border-zinc-700 bg-zinc-800/40 font-mono text-[10px] text-zinc-500">
              probed {timeAgo(flagship.checked_at)}
            </Badge>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0">
        <div className="grid gap-5 lg:grid-cols-5">
          {/* left: identity + ladder */}
          <div className="min-w-0 space-y-4 lg:col-span-3">
            <div className="rounded-lg border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.07] to-transparent p-3.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-mono text-sm font-bold tracking-tight text-zinc-50">
                  {flagship.service}
                </p>
                <p className="font-mono text-sm font-bold text-emerald-400">$0.05<span className="text-[10px] font-normal text-zinc-500">/call</span></p>
              </div>
              <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-400">{flagship.description}</p>
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {flagship.prices.map((p) => (
                  <span
                    key={p.route}
                    className="inline-flex items-center rounded-md border border-zinc-700/60 bg-zinc-800/40 px-2 py-0.5 font-mono text-[10px] text-zinc-400"
                    title={`${p.route} — ${p.note}`}
                  >
                    {p.route} · {p.price}
                  </span>
                ))}
                <span className="inline-flex items-center rounded-md border border-zinc-700/60 bg-zinc-800/40 px-2 py-0.5 font-mono text-[10px] text-zinc-400">
                  {flagship.network}
                </span>
              </div>
            </div>

            <ol
              className="min-w-0 rounded-lg border border-zinc-800/70 bg-zinc-950/40 p-3.5"
              aria-label="Flagship service status ladder"
            >
              {flagship.ladder.map((r, i) => (
                <LadderRung
                  key={r.key}
                  index={i}
                  label={r.label}
                  note={r.note}
                  achieved={r.achieved}
                  isFrontier={i === frontierIdx}
                  lastChange={r.last_change}
                  delay={0.06 * i}
                />
              ))}
            </ol>
          </div>

          {/* right: live probes + unlock */}
          <div className="min-w-0 space-y-3 lg:col-span-2">
            {/* live probe panel */}
            <div className="rounded-lg border border-zinc-800/70 bg-zinc-950/40 p-3.5">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                Live endpoint probes
              </p>
              <ul className="mt-2 space-y-1.5">
                {[
                  {
                    k: "root",
                    label: "GET / (app reachable)",
                    ok: flagship.probes.root_status === 200,
                    value: `HTTP ${flagship.probes.root_status}`,
                  },
                  {
                    k: "wb",
                    label: "GET wallet-balance (402 handshake)",
                    ok: flagship.probes.wallet_balance_402,
                    value: flagship.probes.wallet_balance_402 ? "402 · spec-valid" : "FAIL",
                  },
                  {
                    k: "rb",
                    label: "POST research-brief (402 handshake)",
                    ok: flagship.probes.research_brief_402,
                    value: flagship.probes.research_brief_402 ? "402 · spec-valid" : "FAIL",
                  },
                  ...(flagship.probes.quick_brief_402 !== undefined
                    ? [
                        {
                          k: "qb",
                          label: "POST quick-brief · $0.01 tier (402)",
                          ok: flagship.probes.quick_brief_402,
                          value: flagship.probes.quick_brief_402 ? "402 · spec-valid" : "FAIL",
                        },
                      ]
                    : []),
                ].map((p) => (
                  <li key={p.k} className="flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-1.5 font-mono text-[10px] text-zinc-500">
                      <span
                        className={`size-1.5 shrink-0 rounded-full ${p.ok ? "bg-emerald-400" : "bg-rose-500"}`}
                        aria-hidden="true"
                      />
                      <span className="truncate">{p.label}</span>
                    </span>
                    <span
                      className={`shrink-0 font-mono text-[10px] font-semibold ${p.ok ? "text-emerald-400" : "text-rose-400"}`}
                    >
                      {p.value}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-zinc-800/60 pt-2.5">
                <button
                  type="button"
                  onClick={() => copy("endpoint", `${flagship.public_base}/api/x402/research-brief`, "Endpoint URL")}
                  className="flex min-w-0 items-center gap-1.5 rounded-md border border-zinc-700/60 bg-zinc-800/30 px-2 py-1 font-mono text-[10px] text-zinc-400 transition-colors hover:border-emerald-500/40 hover:text-emerald-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60"
                  title="Copy public endpoint URL"
                  aria-label="Copy public endpoint URL"
                >
                  {copied === "endpoint" ? (
                    <Check className="size-3 shrink-0 text-emerald-400" aria-hidden="true" />
                  ) : (
                    <Copy className="size-3 shrink-0" aria-hidden="true" />
                  )}
                  <span className="truncate">{host}/api/x402/research-brief</span>
                </button>
                <a
                  href={`${flagship.public_base}/api/x402/research-brief`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded-md border border-zinc-700/60 bg-zinc-800/30 p-1.5 text-zinc-500 transition-colors hover:border-emerald-500/40 hover:text-emerald-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60"
                  aria-label="Open endpoint (returns 402 payment challenge)"
                  title="Open endpoint — returns the 402 payment challenge"
                >
                  <ExternalLink className="size-3" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* the single owner unlock */}
            <div
              className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3.5"
              role="note"
            >
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-400">
                The single unlock · 30 seconds
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-amber-100/80">
                Claim <span className="font-semibold">20 test-USDC</span> (Base Sepolia) at{" "}
                <a
                  href="https://faucet.circle.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-amber-300 underline decoration-amber-500/40 underline-offset-2 hover:text-amber-200"
                >
                  faucet.circle.com
                </a>{" "}
                to the mission buyer wallet below. All autonomous faucet paths are bot-blocked
                (reCAPTCHA / Cloudflare) — this is the only manual step between the mission and a
                full testnet settlement proof.
              </p>
              <div className="mt-2.5 flex items-center justify-between gap-2 rounded-md border border-amber-500/25 bg-zinc-950/50 px-2.5 py-1.5">
                <span className="min-w-0 truncate font-mono text-[11px] text-amber-200" title={flagship.buyer_funding.address}>
                  {flagship.buyer_funding.address}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0 text-amber-400/80 hover:text-amber-200"
                  onClick={() => copy("buyer", flagship.buyer_funding.address, "Buyer wallet address")}
                  aria-label="Copy buyer wallet address"
                  title="Copy buyer wallet address"
                >
                  {copied === "buyer" ? (
                    <Check className="size-3.5 text-emerald-400" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </Button>
              </div>
              <p className="mt-1.5 font-mono text-[10px] tabular-nums leading-relaxed text-amber-400/70">
                now: {flagship.buyer_funding.base_sepolia_eth ?? "?"} ETH ·{" "}
                {flagship.buyer_funding.base_sepolia_usdc ?? "?"} USDC —{" "}
                {flagship.buyer_funding.funded_for_settlement_test
                  ? "FUNDED — settlement test ready"
                  : "unfunded (waiting)"}
              </p>
            </div>

            {/* mainnet flip note */}
            <div className="rounded-lg border border-zinc-800/70 bg-zinc-950/40 p-3.5">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                After the proof · mainnet flip
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">{flagship.mainnet_flip}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
